import { Fragment } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Edit } from '@mint-lib/icons';
import {
  Button,
  Card,
  Divider,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { useLocalizedGranularityLevelLabels } from '../../constants/i18n/granularityLevel.js';
import { useDataFileContext } from '../../contexts/DataFileContext/useDataFileContext.js';
import { usePlatformsCatalog } from '../../queries/index.js';

type StaticContextDataV1Props = {
  isEditable: boolean;
  switchToEditingMode: () => void;
};

export const StaticContextDataV1 = ({
  isEditable,
  switchToEditingMode,
}: StaticContextDataV1Props) => {
  const { t } = useTranslation('@myn/import-hub');

  const {
    dataFilePreview: {
      currentStep,
      context: { journeyName, platformName },
      granularityLevel,
    },
  } = useDataFileContext<'V1'>();

  const isGranularityLevelShown =
    granularityLevel &&
    ['validation', 'channel_assignment'].includes(currentStep);
  const granularityLevelLabelByType = useLocalizedGranularityLevelLabels();

  const granularityLevelLabel = granularityLevelLabelByType[granularityLevel];

  const {
    data: { platformsByName },
  } = usePlatformsCatalog();
  const platform = platformsByName[platformName as string];

  // TODO: Refactor to use <Trans> component
  const contextItems = [
    // eslint-disable-next-line @mint/no-t-concatenation
    { label: t('Campaign') + ': ', value: journeyName },
    // eslint-disable-next-line @mint/no-t-concatenation
    { label: t('Platform') + ': ', value: platform?.label },
    {
      // eslint-disable-next-line @mint/no-t-concatenation
      label: t('Data attribution level') + ': ',
      value: isGranularityLevelShown ? granularityLevelLabel : null,
    },
  ].filter((item) => Boolean(item.value));

  return (
    <Card className="mb-3" contentClassName="flex w-fit gap-4">
      {contextItems.map((item, index) => (
        <Fragment key={item.label}>
          <div className="flex max-w-[272px] flex-col gap-1">
            <span
              className={typographyVariants({
                variant: 'label01',
                color: 'secondary',
              })}
            >
              {item.label}
            </span>
            <TruncateText>
              <span
                className={cn(
                  typographyVariants({ variant: 'h6', color: 'primary' }),
                  'block',
                )}
              >
                {item.value}
              </span>
            </TruncateText>
          </div>

          {index < contextItems.length - 1 && (
            <Divider orientation="vertical" className="h-auto!" />
          )}
        </Fragment>
      ))}
      {isEditable && (
        <Button
          onClick={switchToEditingMode}
          aria-label={t('Edit context fields')}
          data-dd-action-name="Edit context fields"
          icon
          variant="plain"
          size="large"
          className="ml-4"
        >
          <Edit />
        </Button>
      )}
    </Card>
  );
};
